import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Send, FlaskConical, Users } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const SegmentPicker = ({ segments, value, onChange }) => {
  const current = segments.find((s) => s.key === value);
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-muted-foreground">Send to</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="mt-2" data-testid="newsletter-segment-select">
          <SelectValue placeholder="Choose an audience" />
        </SelectTrigger>
        <SelectContent>
          {segments.map((s) => (
            <SelectItem key={s.key} value={s.key} data-testid={`newsletter-segment-option-${s.key}`}>
              {s.label} · {s.count}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {current && (
        <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1.5" data-testid="newsletter-segment-count">
          <Users size={12} /> {current.count} {current.count === 1 ? "recipient" : "recipients"} · unsubscribed addresses are always skipped
        </p>
      )}
    </div>
  );
};

export const NewsletterManager = () => {
  const [subject, setSubject] = useState("");
  const [content, setContent] = useState("");
  const [segment, setSegment] = useState("all");
  const [segments, setSegments] = useState([]);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [history, setHistory] = useState([]);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    const [{ data }, { data: segs }] = await Promise.all([api.get("/admin/newsletter"), api.get("/admin/newsletter/segments")]);
    setHistory(data);
    setSegments(segs);
    if (data.some((n) => n.status === "sending")) {
      clearTimeout(pollRef.current);
      pollRef.current = setTimeout(load, 3000);
    }
  }, []);

  useEffect(() => {
    load();
    return () => clearTimeout(pollRef.current);
  }, [load]);

  const label = segments.find((s) => s.key === segment)?.label || "this audience";
  const ready = subject.trim() && content.trim();

  const send = async () => {
    if (!window.confirm(`Send this newsletter to ${label}?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/newsletter", { subject, content, segment });
      toast.success(`Newsletter sending to ${data.total} ${data.total === 1 ? "recipient" : "recipients"} (${data.segment_label})`);
      setSubject(""); setContent("");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally { setBusy(false); }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/admin/newsletter/test", { subject, content, segment });
      toast.success(`Test sent to ${data.to}`);
    } catch (e) {
      toast.error(apiError(e));
    } finally { setTesting(false); }
  };

  return (
    <div className="space-y-12">
      <section className="silk-card rounded-lg p-6 sm:p-8 max-w-2xl" data-testid="newsletter-composer">
        <h2 className="font-display text-lg md:text-2xl text-foreground mb-1">Community newsletter</h2>
        <p className="text-sm text-muted-foreground mb-6">Choose who hears from you — everyone, seekers, practitioners, or Community members — styled in the O.R.E.C brand. A quarterly note keeps the Ripple alive; you'll get an email reminder at the start of each quarter.</p>
        <div className="space-y-5">
          <SegmentPicker segments={segments} value={segment} onChange={setSegment} />
          <div>
            <Label htmlFor="nl-subject" className="text-xs uppercase tracking-wider text-muted-foreground">Subject</Label>
            <Input id="nl-subject" data-testid="newsletter-subject-input" value={subject} placeholder="Spring on the ripple — new faces, new wisdom"
              onChange={(e) => setSubject(e.target.value)} className="mt-2" />
          </div>
          <div>
            <Label htmlFor="nl-content" className="text-xs uppercase tracking-wider text-muted-foreground">Message</Label>
            <Textarea id="nl-content" data-testid="newsletter-content-input" value={content} rows={9}
              placeholder={"Hello beautiful community,\n\nHere's what's been rippling this quarter..."}
              onChange={(e) => setContent(e.target.value)} className="mt-2" />
          </div>
          <div className="flex flex-wrap gap-3">
            <Button data-testid="newsletter-send-btn" disabled={busy || testing || !ready} onClick={send} className="rounded-full px-6">
              <Send size={14} className="mr-2" /> {busy ? "Sending…" : `Send to ${label.toLowerCase()}`}
            </Button>
            <Button variant="outline" data-testid="newsletter-test-btn" disabled={busy || testing || !ready} onClick={sendTest} className="rounded-full px-6">
              <FlaskConical size={14} className="mr-2" /> {testing ? "Sending test…" : "Send test to me"}
            </Button>
          </div>
        </div>
      </section>

      <section>
        <h2 className="font-display text-lg md:text-2xl text-foreground mb-5">Past newsletters</h2>
        <div className="space-y-2" data-testid="newsletter-history">
          {history.map((n) => (
            <div key={n.id} className="silk-card rounded-lg px-5 py-4 flex flex-wrap items-center justify-between gap-3" data-testid={`newsletter-${n.id}`}>
              <div className="min-w-0">
                <p className="text-sm text-foreground truncate">{n.subject}</p>
                <p className="text-xs text-muted-foreground mt-0.5 flex flex-wrap items-center gap-2">
                  <span>{new Date(n.created_at).toLocaleDateString()}</span>
                  <span className="rounded-full border border-border px-2 py-0.5" data-testid={`newsletter-segment-${n.id}`}>{n.segment_label || "Everyone on the email list"}</span>
                </p>
              </div>
              <span className={`text-xs shrink-0 ${n.status === "sending" ? "text-primary animate-pulse" : "text-muted-foreground"}`}>
                {n.status === "sending" ? `Sending ${n.sent_count}/${n.total}…` : `Delivered ${n.sent_count}/${n.total}`}
              </span>
            </div>
          ))}
          {history.length === 0 && <p className="text-sm text-muted-foreground">No newsletters sent yet.</p>}
        </div>
      </section>
    </div>
  );
};
